import type { ProductFamily } from '../../../types/product.types';
import { TrackedEvent, TrackedEventCase } from '../event-types';
import type { GTMCustomEvent, GTMEventWrapper, GTMQAInteractions, GTMViewProductEvent } from './gtm-types';
import { type ProductVariant, wrapGTMEvent, buildGTMProductDetails } from './gtm-utils.helper';

/*
 * Builds a GTM event for viewing a product details page
 */
export const buildGTMViewProductDetails = (variant: ProductVariant, productFamily: ProductFamily): GTMEventWrapper<GTMViewProductEvent> => {
	return wrapGTMEvent(TrackedEvent.PRODUCT_DETAIL_VIEW, TrackedEventCase.PRODUCT_DETAIL_LOAD, {
		detail: {
			products: [buildGTMProductDetails(variant, productFamily)]
		}
	});
};

/*
 * Builds a GTM event for BazaarVoice analytics on product details page load.
 * Overwritten on each new product page navigation.
 */
export const buildGTMBVProductView = (productFamily: ProductFamily): GTMCustomEvent => ({
	event: TrackedEvent.BV_PRODUCT_VIEW,
	bv: {
		brand: productFamily.brandInfo.name,
		productId: productFamily.productId,
		baseCategory: productFamily.baseCategory.name,
		numReviews: productFamily.reviewSummary.reviewCount,
		numQuestions: 'questionsCount' in productFamily ? productFamily.questionsCount : 0,
		// TODO: EFDC-39910
		// numAnswers: 'answersCount' in productFamily ? productFamily.answersCount : 0,
		avgRating: productFamily.reviewSummary.ratingValue
		// TODO: EFDC-39910
		// percentRecommended: 'percentRecommended' in productFamily ? productFamily.percentRecommended : 0
	}
});

/**
 * Builds a GTM event for selecting a variant
 */
export const buildGTMSelectVariant = (variant: ProductVariant, productFamily: ProductFamily): GTMEventWrapper<GTMViewProductEvent> => {
	return wrapGTMEvent(TrackedEvent.PRODUCT_CLICK, TrackedEventCase.VARIANT_SELECT, {
		detail: {
			products: [buildGTMProductDetails(variant, productFamily)]
		}
	});
};

/**
 * Builds a GTM event for tracking reviews interactions
 */
export const buildGTMReviewsInteractionClick = (variationInteraction: string): GTMCustomEvent => {
	return {
		event: TrackedEvent.PRODUCT_PAGE_EVENTS,
		type: TrackedEventCase.REVIEWS_INTERACTION,
		variationInteraction
	};
};

export const buildGTMReviewsBuySectionLinkClick = (): GTMCustomEvent => {
	return {
		event: TrackedEvent.PRODUCT_PAGE_EVENTS,
		type: TrackedEventCase.REVIEWS_BUY_SECTION_LINK_CLICKED
	};
};

export const buildProductQuantityChange = (isNested: boolean): GTMCustomEvent => ({
	event: TrackedEvent.PRODUCT_PAGE_EVENTS,
	type: TrackedEventCase.QUANTITY_CHANGE,
	input: isNested ? 'nested-product-quantity' : 'main-quantity'
});

/* Review + Q&A */
export const buildGTMReviewsExpanded = (): GTMCustomEvent => ({
	event: TrackedEvent.REVIEWS_AND_QA_EXPANDABLES,
	type: TrackedEventCase.REVIEWS_EXPANDABLE_OPENED
});

export const buildGTMQAExpanded = (): GTMCustomEvent => ({
	event: TrackedEvent.REVIEWS_AND_QA_EXPANDABLES,
	type: TrackedEventCase.QA_EXPANDABLE_OPENED
});

export const buildGTMReviewsLoaded = (): GTMCustomEvent => ({
	event: TrackedEvent.REVIEWS_AND_QA_EXPANDABLES,
	type: TrackedEventCase.REVIEWS_LOADED
});

export const buildGTMQALoaded = (): GTMCustomEvent => ({
	event: TrackedEvent.REVIEWS_AND_QA_EXPANDABLES,
	type: TrackedEventCase.QA_LOADED
});

export const buildGTMSpecsExpanded = (): GTMCustomEvent => ({
	event: TrackedEvent.SPECS_EXPANDABLE,
	type: TrackedEventCase.SPECS_EXPANDABLE_OPENED
});

export const buildGTMQAInteractions = (variationInteraction: GTMQAInteractions): GTMCustomEvent => ({
	event: TrackedEvent.PRODUCT_PAGE_EVENTS,
	type: TrackedEventCase.QA_INTERACTION,
	variationInteraction
});

/** Builds a GTM event for manufacturer resource clicks */
export const buildGTMManufacturerResourceClick = (variationInteraction: string): GTMCustomEvent => ({
	event: TrackedEvent.PRODUCT_PAGE_EVENTS,
	type: TrackedEventCase.MANUFACTURER_RESOURCE_CLICKED,
	variationInteraction
});
